import { useEffect, useState } from "react";
import { getConfig, logout } from "../api/client";
import { clearStoredHistory } from "../hooks/useConversation";
import type { JarvisSettings } from "../hooks/useSettings";
import type { AppConfig } from "../types";

interface SettingsScreenProps {
  settings: JarvisSettings;
  onUpdateSettings: (patch: Partial<JarvisSettings>) => void;
  onBack: () => void;
  onLoggedOut: () => void;
}

export function SettingsScreen({
  settings,
  onUpdateSettings,
  onBack,
  onLoggedOut,
}: SettingsScreenProps) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [connectionOk, setConnectionOk] = useState<boolean | null>(null);
  const [micStatus, setMicStatus] = useState<"granted" | "unknown">("unknown");
  const [historyCleared, setHistoryCleared] = useState(false);

  useEffect(() => {
    getConfig()
      .then((data) => {
        setConfig(data);
        setConnectionOk(true);
      })
      .catch(() => setConnectionOk(false));

    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: "microphone" as PermissionName })
        .then((status) => setMicStatus(status.state === "granted" ? "granted" : "unknown"))
        .catch(() => setMicStatus("unknown"));
    }
  }, []);

  const handleClearHistory = () => {
    clearStoredHistory();
    setHistoryCleared(true);
    setTimeout(() => setHistoryCleared(false), 2000);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      onLoggedOut();
    }
  };

  const voices = config?.ttsVoices ?? [settings.voice];

  return (
    <div className="screen settings-screen">
      <div className="settings-header">
        <button className="back-button" type="button" aria-label="Späť" onClick={onBack}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="settings-title">Nastavenia</div>
      </div>

      <div className="settings-section">
        <div className="settings-row">
          <span className="settings-label">Pripojenie k serveru</span>
          <span className="settings-value">
            <span className={`status-dot ${connectionOk ? "online" : "offline"}`} />
            {connectionOk === null ? "Zisťujem…" : connectionOk ? "Pripojené" : "Nedostupné"}
          </span>
        </div>
        <div className="settings-row">
          <span className="settings-label">Mikrofón</span>
          <span className="settings-value">
            {micStatus === "granted" ? "Povolený" : "Zatiaľ nevyžiadaný"}
          </span>
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-row">
          <span className="settings-label">Hlas asistenta</span>
          <select
            className="settings-select"
            value={settings.voice}
            onChange={(event) => onUpdateSettings({ voice: event.target.value })}
          >
            {voices.map((voice) => (
              <option key={voice} value={voice}>
                {voice}
              </option>
            ))}
          </select>
        </div>
        <div className="settings-row">
          <span className="settings-label">Rýchlosť reči ({settings.playbackRate.toFixed(2)}×)</span>
          <input
            className="settings-slider"
            type="range"
            min={0.75}
            max={1.5}
            step={0.05}
            value={settings.playbackRate}
            onChange={(event) => onUpdateSettings({ playbackRate: Number(event.target.value) })}
          />
        </div>
        <div className="settings-row">
          <span className="settings-label">Zvukové efekty rozhrania</span>
          <button
            type="button"
            className={`toggle ${settings.soundEffectsEnabled ? "on" : ""}`}
            role="switch"
            aria-checked={settings.soundEffectsEnabled}
            onClick={() => onUpdateSettings({ soundEffectsEnabled: !settings.soundEffectsEnabled })}
          >
            <span className="toggle-knob" />
          </button>
        </div>
      </div>

      <div className="settings-section">
        <button className="settings-action" type="button" onClick={handleClearHistory}>
          {historyCleared ? "História bola vymazaná" : "Vymazať históriu konverzácie"}
        </button>
        <div className="settings-note">
          Hlasové požiadavky (prepis reči, odpovede a generovanie hlasu) sa spracúvajú
          prostredníctvom externých služieb OpenAI API. História konverzácie sa ukladá iba
          lokálne v tomto prehliadači.
        </div>
      </div>

      <div className="settings-section">
        <button className="settings-action danger" type="button" onClick={handleLogout}>
          Odhlásiť sa
        </button>
      </div>
    </div>
  );
}
